import type { QuestionnaireItem, QuestionnaireResponseItem } from "fhir/r3"
import { enableWhenConditionsMet } from "../components/question/enableWhenConditionsMet"

export function getHint(question: QuestionnaireItem): string {
  return (question.extension ?? []).filter((extension) => extension.url.includes("designNote"))[0]?.valueMarkdown ?? ""
}

export function questionIsAnswered(question: QuestionnaireItem, responses: QuestionnaireResponseItem[]): boolean {
  if (question.type === "display") return true

  const responsesForQuestion = getResponseForQuestion(question, responses)
  if (question.type === "group") {
    return (question.item ?? []).every((item) => questionIsAnswered(item, responses))
  }

  const questionIsInUse =
    question.enableWhen == null ? true : enableWhenConditionsMet(question.enableWhen ?? [], responses)
  if (questionIsInUse && question.required === true) {
    if (["string", "text", "choice", "open-choice"].includes(question.type)) {
      return ((responsesForQuestion?.answer ?? [])[0]?.valueString ?? "").trim().length > 0
    }
    if (question.type === "boolean") {
      return (responsesForQuestion?.answer ?? [])[0]?.valueBoolean != null
    }

    return false
  }

  return true
}

export function getChoiceOptions(question: QuestionnaireItem): string[] {
  return question.option?.map((option) => option.valueString ?? "") ?? []
}

export function getResponseForQuestion(
  question: QuestionnaireItem,
  responses: QuestionnaireResponseItem[],
): QuestionnaireResponseItem | undefined {
  return getResponseForLinkId(question.linkId, responses)
}
export function getResponseForLinkId(
  linkId: string,
  responses: QuestionnaireResponseItem[],
): QuestionnaireResponseItem | undefined {
  return responses.find((response) => response.linkId === linkId)
}

export function getStringValueFromResponse(questionnaireResponseItem?: QuestionnaireResponseItem): string {
  const answers = questionnaireResponseItem?.answer ?? []
  if (answers.length > 0) {
    return answers[0]?.valueString ?? ""
  }
  return ""
}

export function getBooleanValueFromResponse(questionnaireResponseItem?: QuestionnaireResponseItem): string {
  const answers = questionnaireResponseItem?.answer ?? []
  if (answers.length > 0) {
    if (Object.keys(answers[0]).includes("valueBoolean")) {
      return (answers[0]?.valueBoolean ?? false) ? "yes" : "no"
    }
  }
  return ""
}

export interface ChoiceAnswer {
  choices: string[]
  other: string
}
export function getChoiceAnswerFromResponse(
  choiceOptions: string[],
  questionnaireResponseItem?: QuestionnaireResponseItem,
): ChoiceAnswer {
  const answers = (questionnaireResponseItem?.answer ?? []).map((value) => value.valueString ?? "")
  const choices = answers.filter((answer) => choiceOptions.includes(answer))
  const other = answers.find((answer) => !choiceOptions.includes(answer)) ?? ""
  return { choices, other }
}

/**
 * Recursively walks through an array of QuestionnaireResponseItems and returns
 * a flattened array of all answer values (regardless of type).
 *
 * @param items An array of QuestionnaireResponseItems to search.
 * @returns An array containing all found answer values.
 */
export function getFlattenedAnswerValues(
  items: QuestionnaireResponseItem[],
): Array<string | number | boolean | undefined> {
  let values: Array<string | number | boolean | undefined> = []

  items.forEach((item) => {
    if (item.answer && Array.isArray(item.answer)) {
      item.answer.forEach((a) => {
        const answerRecord = a as Record<string, unknown> // prevents a[key] ts error: 'any' type because expression of type 'string' can't be used to index type
        Object.keys(answerRecord).forEach((key) => {
          if (key.startsWith("value") && key in answerRecord) {
            const val = answerRecord[key]
            values.push(val as "string | number | boolean | undefined")
          }
        })
        // If this answer has nested items, process them recursively.
        if (answerRecord.item && Array.isArray(answerRecord.item)) {
          values = values.concat(getFlattenedAnswerValues(answerRecord.item))
        }
      })
    }
  })

  return values
}
